import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useApolloClient, useQuery, useMutation } from '@apollo/client'
import AsyncSelect from 'react-select/async'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import MainLayout from '../../components/Layout'
import Button from '../../components/atoms/Buttons'
import { FaRegCalendarAlt } from 'react-icons/fa'
import { useUserContext } from '../../context/UserContext'
import { LIST_EVENTS } from '../../apollo/queries/event'
import {
  LIST_BOOKINGS_BY_DATE,
  GET_HORSES_BY_IDS,
  GET_AVAILABILITY,
  SEARCH_CUSTOMERS_FOR_ADMIN,
  LIST_HORSES_FOR_USER,
} from '../../apollo/queries/booking'
import {
  CREATE_BOOKING,
  CANCEL_BOOKING,
  UPDATE_BOOKING,
  RESET_CUSTOMER_NO_SHOW_COUNT,
} from '../../apollo/mutations/booking'
import RescheduleBookingModal from './RescheduleBookingModal'
import EditBookingDetailsModal from './EditBookingDetailsModal'
import RefundConfirmationModal from './RefundConfirmationModal'
import CollectBalanceModal from './CollectBalanceModal'
import SendInvoiceModal from './SendInvoiceModal'
import './style.css'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.extend(duration)
dayjs.extend(relativeTime)

const DEFAULT_TZ = 'America/Chicago'
const POLL_MS = 15000

/** Event fully ended: endDate (or startDate) YYYY-MM-DD is strictly before today. */
function isPastEvent(ev, todayYmd) {
  const end = (ev.endDate || ev.startDate || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return false
  return end < todayYmd
}

/** Full scan charged in the first checkout (admin bookings or event "pay in full" flag). */
function isFullAmountAtCheckout(b) {
  return (
    b.scanAmount != null &&
    b.balanceDue === 0 &&
    b.depositAmount === b.scanAmount &&
    b.scanAmount > 0
  )
}

const STATUS_STYLES = {
  PENDING: 'status-badge status-badge--pending',
  CONFIRMED: 'status-badge status-badge--confirmed',
  CHECKED_IN: 'status-badge status-badge--default',
  COMPLETED: 'status-badge status-badge--completed',
  NO_SHOW: 'status-badge status-badge--cancelled',
  CANCELLED: 'status-badge status-badge--default',
}

const STATUS_LABELS = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  CHECKED_IN: 'Checked In',
  COMPLETED: 'Completed',
  NO_SHOW: 'No Show',
  CANCELLED: 'Cancelled',
}

const STATUS_FILTERS = ['ALL', 'PENDING', 'CONFIRMED', 'CHECKED_IN', 'COMPLETED', 'NO_SHOW', 'CANCELLED']

// ---------- Book For A Customer modal ----------
function BookForCustomerModal({ event, onClose }) {
  const { openMessageModal } = useUserContext()
  const client = useApolloClient()
  const searchDebounceRef = useRef(null)
  const zone = event?.timezone || DEFAULT_TZ
  const minDate = (event.startDate || dayjs().format('YYYY-MM-DD')).slice(0, 10)
  const maxDate = (event.endDate || event.startDate || '').slice(0, 10) || undefined
  const [date, setDate] = useState(minDate)
  const [selectedSlot, setSelectedSlot] = useState('')
  const [linkedUserId, setLinkedUserId] = useState(null)
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null)
  const [selectedHorseIds, setSelectedHorseIds] = useState([])
  const [notes, setNotes] = useState('')

  const { data: avail, loading: availLoading } = useQuery(GET_AVAILABILITY, {
    variables: { eventId: event.id, date },
    skip: !date,
    fetchPolicy: 'network-only',
  })

  const {
    data: horsesData,
    loading: horsesLoading,
    error: horsesError,
  } = useQuery(LIST_HORSES_FOR_USER, {
    variables: {
      userId: linkedUserId || '',
      email: selectedCustomerOption?.user?.email || null,
      limit: 100,
    },
    skip: !linkedUserId,
    fetchPolicy: 'network-only',
  })

  const [createBooking, { loading }] = useMutation(CREATE_BOOKING, {
    refetchQueries: ['listBookingsByDate'],
  })

  const loadCustomerOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
        searchDebounceRef.current = setTimeout(async () => {
          const v = (inputValue || '').trim()
          if (v.length < 2) {
            resolve([])
            return
          }
          try {
            const { data } = await client.query({
              query: SEARCH_CUSTOMERS_FOR_ADMIN,
              variables: { query: v, limit: 25 },
              fetchPolicy: 'network-only',
            })
            const rows = (data?.searchCustomersForAdmin || []).filter((u) => u?.id)
            resolve(
              rows.map((u) => {
                const display =
                  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
                  u.name ||
                  '—'
                return {
                  value: u.id,
                  label: `${display} · ${u.email || ''}`,
                  user: u,
                }
              })
            )
          } catch {
            resolve([])
          }
        }, 300)
      }),
    [client]
  )

  const onCustomerSelect = (opt) => {
    setSelectedHorseIds([])
    setSelectedCustomerOption(opt)
    if (!opt) {
      setLinkedUserId(null)
      return
    }
    setLinkedUserId(opt.user.id)
  }

  const toggleHorse = (horseId) => {
    setSelectedHorseIds((prev) =>
      prev.includes(horseId) ? prev.filter((id) => id !== horseId) : [...prev, horseId]
    )
  }

  const customerHorses = horsesData?.listHorsesForUser?.items || []

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!selectedSlot) {
      openMessageModal('Heads Up', 'Pick a time slot first.')
      return
    }
    const u = selectedCustomerOption?.user
    if (!linkedUserId || !u?.email) {
      openMessageModal(
        'Heads Up',
        'Choose a customer from the directory. Bookings require an existing profile with an email.'
      )
      return
    }
    if (!selectedHorseIds.length) {
      openMessageModal('Heads Up', 'Select at least one horse for this fitting.')
      return
    }
    const displayName =
      [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
      (u.name ? String(u.name).trim() : '') ||
      String(u.email).trim()
    try {
      const input = {
        eventId: event.id,
        customerName: displayName,
        customerEmail: String(u.email).trim(),
        customerPhone: u.phone ? String(u.phone).trim() : null,
        startTime: selectedSlot,
        notes: notes.trim() || null,
        horseIds: selectedHorseIds,
        linkedUserId,
      }
      const { data } = await createBooking({
        variables: { input },
      })
      const checkoutUrl = data?.createBooking?.checkoutUrl
      openMessageModal(
        "You're All Set.",
        checkoutUrl
          ? `Booking created (PENDING). Send the customer this link to pay in full, or mark Confirmed in the run sheet:\n\n${checkoutUrl}`
          : 'Booking created.'
      )
      onClose()
    } catch (err) {
      openMessageModal('Something Went Sideways', err?.message || 'Mind trying again?')
    }
  }

  const fmtSlot = (iso) => dayjs(iso).tz(zone).format('h:mm A')
  const field = 'booking-form-field'
  const label = 'booking-form-label'
  const slots = avail?.getAvailability || []

  return (
    <div>
      <div className="booking-modal-backdrop" onClick={onClose} />
      <div className="booking-modal-container booking-modal-container--customer">
        <h2 className="book-for-customer-modal__title">Book For A Customer</h2>
        <div className="book-for-customer-modal__event">{event.eventName}</div>
        <div className="book-for-customer-modal__meta">
          Sessions are {event.durationMinutes || 60} min
          {Number(event.bufferMinutes) > 0
            ? ` with ${event.bufferMinutes} min buffer.`
            : '.'}
        </div>
        <form onSubmit={onSubmit} className="space-y-[18px]">
          <div>
            <div className={label}>Pick A Day</div>
            <div className="booking-date-field-wrap">
              <input
                type="date"
                className={field}
                value={date}
                min={minDate}
                max={maxDate}
                onChange={(e) => { setDate(e.target.value); setSelectedSlot('') }}
              />
              <span className="booking-date-field-wrap__icon" aria-hidden>
                <FaRegCalendarAlt />
              </span>
            </div>
          </div>
          <div>
            <div className={label}>Choose Time</div>
            {availLoading ? (
              <div className="text-[12px] text-[color:var(--color-gray-text)] mt-2">Loading slots…</div>
            ) : slots.length === 0 ? (
              <div className="text-[12px] text-[color:var(--color-gray-text)] mt-2">No openings that day.</div>
            ) : (
              <div className="booking-modal-slot-grid">
                {slots.map((s) => (
                  <button
                    type="button"
                    key={s.startTime}
                    onClick={() => setSelectedSlot(s.startTime)}
                    className={`booking-modal-slot${selectedSlot === s.startTime ? ' booking-modal-slot--selected' : ''}`}
                  >
                    {fmtSlot(s.startTime)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className={label}>Customer (from directory)</div>
            <AsyncSelect
              instanceId="booking-customer-async"
              className="booking-customer-lookup"
              classNamePrefix="booking-customer-async"
              placeholder="Search by name or email (2+ characters)…"
              isClearable
              cacheOptions
              defaultOptions={false}
              loadOptions={loadCustomerOptions}
              value={selectedCustomerOption}
              onChange={onCustomerSelect}
              noOptionsMessage={({ inputValue }) =>
                (inputValue || '').trim().length < 2
                  ? 'Enter at least 2 characters'
                  : 'No matches'
              }
            />
            <p className="booking-customer-lookup-hint booking-customer-lookup-hint--muted">
              Only customers with an app profile can be booked here; contact details come from their account.
            </p>
            {selectedCustomerOption?.user && (
              <div className="booking-selected-customer">
                <div className="booking-selected-customer__row">
                  <span className="booking-selected-customer__k">Name</span>
                  <span className="booking-selected-customer__v">
                    {[selectedCustomerOption.user.firstName, selectedCustomerOption.user.lastName]
                      .filter(Boolean)
                      .join(' ')
                      .trim() ||
                      selectedCustomerOption.user.name ||
                      '—'}
                  </span>
                </div>
                <div className="booking-selected-customer__row">
                  <span className="booking-selected-customer__k">Email</span>
                  <span className="booking-selected-customer__v">{selectedCustomerOption.user.email || '—'}</span>
                </div>
                <div className="booking-selected-customer__row">
                  <span className="booking-selected-customer__k">Phone</span>
                  <span className="booking-selected-customer__v">{selectedCustomerOption.user.phone || '—'}</span>
                </div>
              </div>
            )}
          </div>
          {linkedUserId && (
            <div>
              <div className={label}>Horses for this fitting</div>
              {horsesError && (
                <p className="booking-horse-query-error" role="alert">
                  Could not load horses: {horsesError.message}
                </p>
              )}
              {horsesLoading ? (
                <p className="text-[12px] text-[color:var(--color-gray-text)] mt-2">Loading horses…</p>
              ) : horsesError ? null : customerHorses.length === 0 ? (
                <p className="text-[12px] text-[color:var(--color-gray-text)] mt-2 leading-relaxed">
                  No horses on file for this customer. Add a horse to their account before booking.
                </p>
              ) : (
                <ul className="booking-horse-pick-list">
                  {customerHorses.map((h) => {
                    const primary = h.registeredName || h.name || 'Horse'
                    const showBoth = h.registeredName && h.name && h.registeredName !== h.name
                    return (
                      <li key={h.id}>
                        <label className="booking-horse-pick-row">
                          <input
                            type="checkbox"
                            checked={selectedHorseIds.includes(h.id)}
                            onChange={() => toggleHorse(h.id)}
                          />
                          <span className="booking-horse-pick-label">
                            {primary}
                            {showBoth && (
                              <span className="booking-horse-pick-sub"> ({h.name})</span>
                            )}
                          </span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )}
          <div>
            <div className={label}>Notes</div>
            <textarea
              className={`${field} booking-form-field--textarea`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
            />
          </div>
          <div className="booking-modal-actions">
            <button
              type="submit"
              className="booking-modal-btn booking-modal-btn--primary"
              disabled={loading}
            >
              {loading ? 'Saving…' : 'Save Booking'}
            </button>
            <button
              type="button"
              className="booking-modal-btn booking-modal-btn--outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------- KPI card ----------
const KpiCard = ({ label, value, sub, tone = 'default' }) => {
  const toneClass =
    tone === 'good'
      ? 'kpi-card--good'
      : tone === 'bad'
      ? 'kpi-card--bad'
      : 'kpi-card--neutral'
  return (
    <div className={`kpi-card ${toneClass}`}>
      <div className="kpi-card__label">{label}</div>
      <div className="kpi-card__value">{value}</div>
      {sub && <div className="kpi-card__sub">{sub}</div>}
    </div>
  )
}

// ---------- Status badge ----------
const StatusBadge = ({ status, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={STATUS_STYLES[status] || 'status-badge status-badge--default'}
  >
    {STATUS_LABELS[status] || status}
  </button>
)

// ---------- Main page ----------
function BookingManagement() {
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [eventFilter, setEventFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [search, setSearch] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [now, setNow] = useState(dayjs())

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createEventId, setCreateEventId] = useState('')
  const [rescheduleTarget, setRescheduleTarget] = useState(null)
  const [editTarget, setEditTarget] = useState(null)
  const [refundTarget, setRefundTarget] = useState(null)
  const [collectTarget, setCollectTarget] = useState(null)
  const [invoiceTarget, setInvoiceTarget] = useState(null)
  const [openMenuId, setOpenMenuId] = useState(null)

  // Tick clock every 10s for live countdowns.
  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 10000)
    return () => clearInterval(t)
  }, [])

  const { data: eventsData } = useQuery(LIST_EVENTS)
  const events = eventsData?.listEvents?.items || []
  const todayYmd = useMemo(() => now.format('YYYY-MM-DD'), [now])
  const { activeEvents, archivedEvents } = useMemo(() => {
    const active = []
    const archived = []
    events.forEach((ev) => {
      (isPastEvent(ev, todayYmd) ? archived : active).push(ev)
    })
    return { activeEvents: active, archivedEvents: archived }
  }, [events, todayYmd])
  const eventMap = useMemo(() => {
    const m = {}
    events.forEach((e) => (m[e.id] = e))
    return m
  }, [events])

  const { data: bookingsData, loading: bookingsLoading, refetch } = useQuery(LIST_BOOKINGS_BY_DATE, {
    variables: { date: selectedDate },
    fetchPolicy: 'network-only',
    pollInterval: autoRefresh ? POLL_MS : 0,
  })
  const allBookings = bookingsData?.listBookingsByDate?.items || []

  // Pull horse names for everything on the day in one batch.
  const horseIds = useMemo(() => {
    const set = new Set()
    allBookings.forEach((b) => (b.horseIds || []).forEach((id) => set.add(id)))
    return Array.from(set)
  }, [allBookings])

  const { data: horsesData } = useQuery(GET_HORSES_BY_IDS, {
    variables: { ids: horseIds },
    skip: horseIds.length === 0,
    fetchPolicy: 'cache-and-network',
  })
  const horseMap = useMemo(() => {
    const m = {}
    ;(horsesData?.getHorsesByIds || []).forEach((h) => (m[h.id] = h))
    return m
  }, [horsesData])

  const [updateBooking] = useMutation(UPDATE_BOOKING, { refetchQueries: ['listBookingsByDate'] })
  const [cancelBooking] = useMutation(CANCEL_BOOKING, { refetchQueries: ['listBookingsByDate'] })
  const [resetNoShow] = useMutation(RESET_CUSTOMER_NO_SHOW_COUNT)

  // Admin override: clear a customer's no-show penalty so they book on normal terms.
  const handleWaiveNoShow = async (booking) => {
    if (!booking.userId || booking.userId === 'guest') {
      window.alert('This booking has no linked customer profile to waive.')
      return
    }
    if (!window.confirm(`Waive no-show penalty for ${booking.customerName}? They'll be able to book with the normal deposit again.`)) return
    try {
      await resetNoShow({ variables: { userId: booking.userId } })
      window.alert('No-show penalty waived.')
    } catch (err) {
      window.alert(err?.message || 'Could not waive penalty — try again.')
    }
  }

  // ---------- Filtering ----------
  const filtered = useMemo(() => {
    let rows = allBookings.slice()
    if (eventFilter) rows = rows.filter((b) => b.eventId === eventFilter)
    if (statusFilter !== 'ALL') rows = rows.filter((b) => b.status === statusFilter)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      rows = rows.filter(
        (b) =>
          (b.customerName || '').toLowerCase().includes(q) ||
          (b.customerEmail || '').toLowerCase().includes(q) ||
          (b.customerPhone || '').toLowerCase().includes(q)
      )
    }
    rows.sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
    return rows
  }, [allBookings, eventFilter, statusFilter, search])

  // ---------- KPIs ----------
  const kpis = useMemo(() => {
    const nonCancelled = allBookings.filter((b) => b.status !== 'CANCELLED')
    const checkedIn = allBookings.filter((b) => b.status === 'CHECKED_IN' || b.status === 'COMPLETED')
    const noShow = allBookings.filter((b) => b.status === 'NO_SHOW')
    const pending = allBookings.filter((b) => b.status === 'PENDING')
    const depositsCollected = allBookings.reduce(
      (sum, b) => (b.depositPaidAt ? sum + (b.depositAmount || 0) : sum),
      0
    )
    // Capacity utilization (events represented today)
    const eventIdsToday = new Set(allBookings.map((b) => b.eventId))
    let totalCapacity = 0
    eventIdsToday.forEach((id) => {
      const ev = eventMap[id]
      if (ev) totalCapacity += Number(ev.capacity || 1)
    })
    const utilization = totalCapacity > 0 ? Math.round((nonCancelled.length / totalCapacity) * 100) : 0

    // On-schedule: compare latest CHECKED_IN/COMPLETED actual vs scheduled
    let scheduleDelta = null
    const lastCheckedIn = [...allBookings]
      .filter((b) => b.checkedInAt)
      .sort((a, b) => new Date(b.checkedInAt) - new Date(a.checkedInAt))[0]
    if (lastCheckedIn) {
      scheduleDelta = dayjs(lastCheckedIn.checkedInAt).diff(dayjs(lastCheckedIn.startTime), 'minute')
    }

    // Next up
    const upcoming = allBookings
      .filter((b) => b.status === 'CONFIRMED' && new Date(b.startTime) > now.toDate())
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))[0]

    return {
      todayCount: nonCancelled.length,
      checkedInCount: checkedIn.length,
      noShowCount: noShow.length,
      pendingCount: pending.length,
      depositsCollected,
      utilization,
      scheduleDelta,
      nextUp: upcoming,
    }
  }, [allBookings, eventMap, now])

  // ---------- Now Serving / On Deck ----------
  const nowServing = useMemo(() => {
    return [...allBookings]
      .filter((b) => b.status === 'CHECKED_IN')
      .sort((a, b) => new Date(b.checkedInAt || 0) - new Date(a.checkedInAt || 0))[0]
  }, [allBookings])

  const onDeck = useMemo(() => {
    return [...allBookings]
      .filter((b) => b.status === 'CONFIRMED')
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))[0]
  }, [allBookings])

  // ---------- Action helpers ----------
  const setStatus = async (booking, status) => {
    try {
      await updateBooking({ variables: { id: booking.id, input: { status } } })
      // After Mark Done, auto-open the Collect Balance modal if balance is owed.
      if (status === 'COMPLETED' && booking.balanceDue > 0 && !booking.balancePaidAt) {
        setCollectTarget(booking)
      }
    } catch (err) {
      alert(err?.message || 'Could not update status')
    }
  }
  const handleCancel = async (booking) => {
    if (!window.confirm('Cancel this booking? No refund will be issued.')) return
    try {
      await cancelBooking({ variables: { id: booking.id } })
    } catch (err) {
      alert(err?.message || 'Could not cancel')
    }
  }

  const horseNames = (b) =>
    (b.horseIds || [])
      .map((id) => horseMap[id]?.name)
      .filter(Boolean)
      .join(', ')

  const fmtTime = (iso, zone) => dayjs(iso).tz(zone || DEFAULT_TZ).format('h:mm A z')
  const fmtDate = (iso, zone) => dayjs(iso).tz(zone || DEFAULT_TZ).format('ddd, MMM D')
  const stripeUrl = (pi) => `https://dashboard.stripe.com/test/payments/${pi}`

  // ---------- Primary one-click action per row ----------
  const PrimaryAction = ({ b }) => {
    if (b.status === 'PENDING') {
      return (
        <button
          onClick={() => setStatus(b, 'CONFIRMED')}
          className="action-btn action-btn--confirm"
        >
          Confirm
        </button>
      )
    }
    if (b.status === 'CONFIRMED') {
      return (
        <button
          onClick={() => setStatus(b, 'CHECKED_IN')}
          className="action-btn action-btn--checkin"
        >
          Check In
        </button>
      )
    }
    if (b.status === 'CHECKED_IN') {
      return (
        <button
          onClick={() => setStatus(b, 'COMPLETED')}
          className="action-btn action-btn--complete"
        >
          Mark Done
        </button>
      )
    }
    return <span className="text-[12px] text-[color:var(--color-gray-text)]">—</span>
  }

  return (
    <MainLayout>
      <div className="w-[92%] max-w-[1400px] mx-auto pb-12">
        {/* Header bar */}
        <div className="flex items-start justify-between flex-wrap gap-4 mt-2">
          <div>
            <h1 className="booking-page-title">Booking Management</h1>
            <p className="text-[13px] text-[color:var(--color-gray-text)] font-montserrat">
              Live event-day run sheet · refreshes every {POLL_MS / 1000}s
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <button
              type="button"
              onClick={() => setSelectedDate(dayjs().format('YYYY-MM-DD'))}
              className="px-3 h-[36px] rounded-full border border-[color:var(--color-primary)] text-[color:var(--color-primary)] text-[12px]"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setSelectedDate(dayjs().add(1, 'day').format('YYYY-MM-DD'))}
              className="px-3 h-[36px] rounded-full border border-[color:var(--color-primary)] text-[color:var(--color-primary)] text-[12px]"
            >
              Tomorrow
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 h-[36px] rounded-full border border-[color:var(--color-gray)] text-[12px]"
            />
            <button
              type="button"
              onClick={() => refetch()}
              className="px-3 h-[36px] rounded-full border border-[color:var(--color-secondary)] text-[color:var(--color-secondary)] text-[12px]"
            >
              Refresh
            </button>
            <label className="flex items-center gap-1 text-[11px] text-[color:var(--color-gray-text)]">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto
            </label>
            <Button
              onClick={() => {
                const preferred =
                  eventFilter &&
                  eventMap[eventFilter] &&
                  !isPastEvent(eventMap[eventFilter], todayYmd)
                    ? eventFilter
                    : activeEvents[0]?.id || ''
                setCreateEventId(preferred)
                setShowCreateModal(true)
              }}
              disabled={activeEvents.length === 0}
              className="event-action-btn"
            >
              + Book For Customer
            </Button>
          </div>
        </div>

        {/* Now Serving / On Deck hero strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
          <HeroCard
            label="NOW SERVING"
            booking={nowServing}
            event={nowServing && eventMap[nowServing.eventId]}
            horseNames={nowServing ? horseNames(nowServing) : ''}
            ctaLabel="Mark Done"
            ctaTone="dark"
            onCta={() => nowServing && setStatus(nowServing, 'COMPLETED')}
            now={now}
          />
          <HeroCard
            label="ON DECK"
            booking={onDeck}
            event={onDeck && eventMap[onDeck.eventId]}
            horseNames={onDeck ? horseNames(onDeck) : ''}
            ctaLabel="Check In"
            ctaTone="blue"
            onCta={() => onDeck && setStatus(onDeck, 'CHECKED_IN')}
            now={now}
          />
        </div>

        {/* KPI strip */}
        <div className="flex gap-3 mt-5 overflow-x-auto pb-2">
          <KpiCard label="Today's Bookings" value={kpis.todayCount} />
          <KpiCard label="Checked In" value={kpis.checkedInCount} tone="good" />
          <KpiCard label="No Shows" value={kpis.noShowCount} tone={kpis.noShowCount ? 'bad' : 'default'} />
          <KpiCard label="Pending Pmt" value={kpis.pendingCount} tone={kpis.pendingCount ? 'warn' : 'default'} />
          <KpiCard
            label="Deposits"
            value={`$${(kpis.depositsCollected / 100).toFixed(2)}`}
            tone="good"
          />
          <KpiCard label="Utilization" value={`${kpis.utilization}%`} />
          <KpiCard
            label="On Schedule?"
            value={
              kpis.scheduleDelta === null
                ? '—'
                : kpis.scheduleDelta <= 1
                ? 'On Time'
                : kpis.scheduleDelta > 0
                ? `${kpis.scheduleDelta}m late`
                : `${Math.abs(kpis.scheduleDelta)}m ahead`
            }
            tone={kpis.scheduleDelta === null ? 'default' : kpis.scheduleDelta > 5 ? 'bad' : 'good'}
          />
          <KpiCard
            label="Next Up"
            value={
              kpis.nextUp
                ? dayjs(kpis.nextUp.startTime)
                    .tz(eventMap[kpis.nextUp.eventId]?.timezone || DEFAULT_TZ)
                    .format('h:mm A')
                : '—'
            }
            sub={
              kpis.nextUp
                ? `${kpis.nextUp.customerName} · ${dayjs(kpis.nextUp.startTime).from(now)}`
                : ''
            }
          />
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap gap-3 items-center mt-5">
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="px-3 h-[40px] rounded-[8px] border border-[color:var(--color-gray)] text-[13px]"
          >
            <option value="">All events</option>
            {activeEvents.length > 0 ? (
              <optgroup label="Active">
                {activeEvents.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.eventName}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {archivedEvents.length > 0 ? (
              <optgroup label="Archived">
                {archivedEvents.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.eventName}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
          <input
            type="text"
            placeholder="Search name, email, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 h-[40px] rounded-[8px] border border-[color:var(--color-gray)] text-[13px] flex-1 min-w-[200px] max-w-[320px]"
          />
          <div className="flex gap-1 flex-wrap">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-[11px] border ${
                  statusFilter === s
                    ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]'
                    : 'bg-white text-[color:var(--color-primary)] border-[color:var(--color-gray)]'
                }`}
              >
                {s === 'ALL' ? 'All' : STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Run sheet table */}
        <div className="mt-5 overflow-x-auto rounded-[12px] border border-[#EAEBED] bg-white">
          {bookingsLoading && allBookings.length === 0 ? (
            <div className="p-10 text-center text-[14px] text-[color:var(--color-gray-text)]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[14px] text-[color:var(--color-gray-text)]">
              Nothing on the books for this day.
            </div>
          ) : (
            <table className="w-full font-montserrat text-[13px]">
              <thead className="bg-[#F1F1F0] text-left text-[11px] uppercase text-[color:var(--color-primary)]">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Horses</th>
                  <th className="px-4 py-3">Event</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3">Due at booking</th>
                  <th className="px-4 py-3">Balance</th>
                  <th className="px-4 py-3 text-right">Action</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const ev = eventMap[b.eventId]
                  const zone = ev?.timezone || DEFAULT_TZ
                  const isPast = new Date(b.startTime) < now.toDate()
                  const needsAttention =
                    isPast && (b.status === 'PENDING' || b.status === 'CONFIRMED')
                  return (
                    <tr
                      key={b.id}
                      className={`border-t border-[#EAEBED] ${
                        needsAttention ? 'bg-yellow-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <div className="font-semibold text-[color:var(--color-primary)]">{fmtTime(b.startTime, zone)}</div>
                        <div className="text-[11px] text-[color:var(--color-gray-text)]">{fmtDate(b.startTime, zone)}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <StatusBadge status={b.status} onClick={() => setOpenMenuId(openMenuId === `s-${b.id}` ? null : `s-${b.id}`)} />
                        {openMenuId === `s-${b.id}` && (
                          <div className="absolute z-30 mt-1 bg-white border border-[#EAEBED] rounded-[8px] shadow-lg">
                            {STATUS_FILTERS.filter((s) => s !== 'ALL').map((s) => (
                              <button
                                key={s}
                                onClick={() => {
                                  setOpenMenuId(null)
                                  setStatus(b, s)
                                }}
                                className="block w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100"
                              >
                                {STATUS_LABELS[s]}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-[color:var(--color-primary)]">{b.customerName}</div>
                        <div className="text-[11px] text-[color:var(--color-secondary)]">
                          <a href={`mailto:${b.customerEmail}`}>{b.customerEmail}</a>
                        </div>
                        {b.customerPhone && (
                          <div className="text-[11px] text-[color:var(--color-secondary)]">
                            <a href={`tel:${b.customerPhone}`}>{b.customerPhone}</a>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {horseNames(b) || <span className="text-[color:var(--color-gray-text)]">—</span>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div>{ev?.eventName || '—'}</div>
                        {ev?.city && <div className="text-[11px] text-[color:var(--color-gray-text)]">{ev.city}, {ev.state}</div>}
                      </td>
                      <td className="px-4 py-3 align-top max-w-[180px]">
                        <div className="text-[12px] text-[#343434] truncate" title={b.notes || ''}>
                          {b.notes || <span className="text-[color:var(--color-gray-text)]">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {b.depositAmount ? (
                          <div className="flex flex-col gap-0.5">
                            {isFullAmountAtCheckout(b) ? (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#6b7280]">
                                Full amount
                              </span>
                            ) : null}
                            <div className="flex items-center gap-1">
                            <span className={b.depositPaidAt ? 'text-green-700' : 'text-red-600'}>
                              ${(b.depositAmount / 100).toFixed(2)}{' '}
                              {b.depositPaidAt ? '✓' : 'due'}
                            </span>
                            {b.stripePaymentIntentId && (
                              <a
                                href={stripeUrl(b.stripePaymentIntentId)}
                                target="_blank"
                                rel="noreferrer"
                                title="Open in Stripe"
                                className="text-[color:var(--color-secondary)] text-[10px] underline"
                              >
                                Stripe
                              </a>
                            )}
                            </div>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {b.balanceDue && b.balanceDue > 0 ? (
                          b.balancePaidAt ? (
                            <span className="text-green-700">
                              ${(b.balanceDue / 100).toFixed(2)} ✓
                            </span>
                          ) : b.invoiceId ? (
                            <a
                              href={b.invoiceHostedUrl || '#'}
                              target="_blank"
                              rel="noreferrer"
                              className="text-yellow-700 underline"
                              title="Open invoice"
                            >
                              ${(b.balanceDue / 100).toFixed(2)} invoiced
                            </a>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <span className="text-red-600 font-semibold">
                                ${(b.balanceDue / 100).toFixed(2)} due
                              </span>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => setCollectTarget(b)}
                                  className="px-2 py-0.5 text-[10px] rounded-full bg-blue-600 text-white"
                                >
                                  Collect
                                </button>
                                <button
                                  onClick={() => setInvoiceTarget(b)}
                                  className="px-2 py-0.5 text-[10px] rounded-full border border-[color:var(--color-secondary)] text-[color:var(--color-secondary)]"
                                >
                                  Invoice
                                </button>
                              </div>
                            </div>
                          )
                        ) : isFullAmountAtCheckout(b) && b.depositPaidAt ? (
                          <span className="text-green-700">Paid in full</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <PrimaryAction b={b} />
                      </td>
                      <td className="px-4 py-3 align-top text-right relative">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === `m-${b.id}` ? null : `m-${b.id}`)}
                          className="text-[color:var(--color-primary)] text-[18px] px-2"
                        >
                          ⋮
                        </button>
                        {openMenuId === `m-${b.id}` && (
                          <div className="absolute right-4 z-30 mt-1 bg-white border border-[#EAEBED] rounded-[8px] shadow-lg w-[180px]">
                            <button onClick={() => { setOpenMenuId(null); setRescheduleTarget(b) }} className="block w-full text-left px-3 py-2 text-[12px] hover:bg-gray-100">Reschedule</button>
                            <button onClick={() => { setOpenMenuId(null); setEditTarget(b) }} className="block w-full text-left px-3 py-2 text-[12px] hover:bg-gray-100">Edit Details</button>
                            {b.balanceDue > 0 && !b.balancePaidAt && (
                              <>
                                <button onClick={() => { setOpenMenuId(null); setCollectTarget(b) }} className="block w-full text-left px-3 py-2 text-[12px] text-blue-700 hover:bg-blue-50">Collect Balance (QR)</button>
                                <button onClick={() => { setOpenMenuId(null); setInvoiceTarget(b) }} className="block w-full text-left px-3 py-2 text-[12px] text-blue-700 hover:bg-blue-50">Send Invoice</button>
                              </>
                            )}
                            <button onClick={() => { setOpenMenuId(null); setStatus(b, 'NO_SHOW') }} className="block w-full text-left px-3 py-2 text-[12px] hover:bg-gray-100">Mark No-Show</button>
                            {b.userId && b.userId !== 'guest' && (
                              <button onClick={() => { setOpenMenuId(null); handleWaiveNoShow(b) }} className="block w-full text-left px-3 py-2 text-[12px] hover:bg-gray-100">Waive No-Show Penalty</button>
                            )}
                            {b.stripePaymentIntentId && b.depositPaidAt && (
                              <button onClick={() => { setOpenMenuId(null); setRefundTarget(b) }} className="block w-full text-left px-3 py-2 text-[12px] text-orange-700 hover:bg-orange-50">Refund + Cancel</button>
                            )}
                            <button onClick={() => { setOpenMenuId(null); handleCancel(b) }} className="block w-full text-left px-3 py-2 text-[12px] text-red-600 hover:bg-red-50">Cancel (no refund)</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreateModal && eventMap[createEventId] && (
        <BookForCustomerModal
          key={createEventId}
          event={eventMap[createEventId]}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      {rescheduleTarget && (
        <RescheduleBookingModal
          booking={rescheduleTarget}
          event={eventMap[rescheduleTarget.eventId]}
          onClose={() => setRescheduleTarget(null)}
          onDone={() => setRescheduleTarget(null)}
        />
      )}
      {editTarget && (
        <EditBookingDetailsModal
          booking={editTarget}
          onClose={() => setEditTarget(null)}
          onDone={() => setEditTarget(null)}
        />
      )}
      {refundTarget && (
        <RefundConfirmationModal
          booking={refundTarget}
          onClose={() => setRefundTarget(null)}
          onDone={() => setRefundTarget(null)}
        />
      )}
      {collectTarget && (
        <CollectBalanceModal
          booking={collectTarget}
          onClose={() => setCollectTarget(null)}
          onDone={() => setCollectTarget(null)}
        />
      )}
      {invoiceTarget && (
        <SendInvoiceModal
          booking={invoiceTarget}
          onClose={() => setInvoiceTarget(null)}
          onDone={() => setInvoiceTarget(null)}
        />
      )}
    </MainLayout>
  )
}

// ---------- Hero card ----------
const HeroCard = ({ label, booking, event, horseNames, ctaLabel, ctaTone, onCta, now }) => {
  if (!booking) {
    return (
      <div className="rounded-[16px] border border-[#EAEBED] bg-white p-5 min-h-[140px] flex flex-col justify-center items-center">
        <div className="text-[11px] uppercase tracking-wide text-[color:var(--color-gray-text)] font-montserrat">{label}</div>
        <div className="text-[14px] text-[color:var(--color-gray-text)] mt-2">No one</div>
      </div>
    )
  }
  const zone = event?.timezone || DEFAULT_TZ
  const scheduled = dayjs(booking.startTime).tz(zone).format('h:mm A z')
  const countdown = dayjs(booking.startTime).from(now)
  const heroBg =
    label === 'NOW SERVING' ? 'bg-blue-50 border-blue-300' : 'bg-green-50 border-green-300'
  const ctaBg =
    ctaTone === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-800 hover:bg-black'
  return (
    <div className={`rounded-[16px] border-2 p-5 ${heroBg} flex flex-col justify-between min-h-[140px]`}>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--color-primary)] font-bold font-montserrat">{label}</div>
        <div className="text-[22px] font-bold text-[color:var(--color-primary)] mt-1">{booking.customerName}</div>
        <div className="text-[12px] text-[#343434] mt-0.5">
          {scheduled} · {countdown}
          {event?.eventName ? ` · ${event.eventName}` : ''}
        </div>
        {horseNames && (
          <div className="text-[12px] text-[color:var(--color-secondary)] mt-0.5">🐴 {horseNames}</div>
        )}
        {booking.notes && (
          <div className="text-[11px] text-[color:var(--color-gray-text)] mt-1 line-clamp-2">"{booking.notes}"</div>
        )}
      </div>
      <button
        onClick={onCta}
        className={`mt-3 self-start px-5 py-2 rounded-full text-white text-[13px] font-semibold ${ctaBg}`}
      >
        {ctaLabel}
      </button>
    </div>
  )
}

export default BookingManagement
